
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { Card } from '@/components/ui/card';

declare global {
  interface Window {
    deferredPrompt: any;
  }
}

export function PWAInstallPrompt() {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [installable, setInstallable] = useState(false);
  
  useEffect(() => {
    // Check if the app is already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                          (window.navigator as any).standalone ||
                          document.referrer.includes('android-app://');
                          
    if (isStandalone) {
      console.log('App is already installed');
      setInstallable(false);
      return;
    }
    
    // Setup before install prompt
    const beforeInstallHandler = (e: Event) => {
      // Prevent Chrome 76+ from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      window.deferredPrompt = e;
      setInstallable(true);
      setShowInstallPrompt(true);
      console.log('App can be installed, saving prompt');
    };
    
    // Setup app installed handler
    const appInstalledHandler = () => {
      console.log('App was installed');
      setInstallable(false);
      setShowInstallPrompt(false);
      window.deferredPrompt = null;
    };
    
    window.addEventListener('beforeinstallprompt', beforeInstallHandler);
    window.addEventListener('appinstalled', appInstalledHandler);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', beforeInstallHandler);
      window.removeEventListener('appinstalled', appInstalledHandler);
    };
  }, []);
  
  const handleInstallClick = async () => {
    if (!window.deferredPrompt) {
      return;
    }
    
    try {
      // Show the installation prompt
      window.deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const choiceResult = await window.deferredPrompt.userChoice;
      
      // Reset the deferred prompt variable
      window.deferredPrompt = null;
      setShowInstallPrompt(false);
      
      // Log the user's choice
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
        
        // After a dismissal, wait and show again later
        setTimeout(() => {
          if (installable) {
            setShowInstallPrompt(true);
          }
        }, 1000 * 60 * 60); // 1 hour
      }
    } catch (error) {
      console.error('Error during install prompt:', error);
    }
  };
  
  if (!showInstallPrompt) {
    return null;
  }
  
  return (
    <Card className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-72 p-3 bg-white shadow-lg z-50">
      <div className="flex items-center justify-between space-x-2">
        <div className="flex-1">
          <p className="font-medium text-sm">Install the app for offline access</p>
          <p className="text-xs text-gray-500">Use even without internet</p>
        </div>
        <Button size="sm" onClick={handleInstallClick}>
          <Download className="mr-1 h-4 w-4" />
          Install
        </Button>
      </div>
    </Card>
  );
}

export default PWAInstallPrompt;
